import type { CSS } from '../../stitches.config';
import type { SpacingScale } from './sizes';
import { sizing } from '../../common/sizing';

export type ContextProp = 'application' | 'content' | 'section';
export type GutterSpacing = SpacingScale;
export type GutterProp =
  | 'all'
  | 'bottom'
  | 'horizontal'
  | 'left'
  | 'none'
  | 'right'
  | 'top'
  | 'vertical';

export type GutterProps = {
  context?: ContextProp;
  gutter?: GutterProp;
};

/** Single source of truth for ends (top/bottom) and sides (left/right) tokens per context. */
const GUTTER_CONTEXT_TOKENS = {
  section: { ends: '$6', sides: sizing.sides },
  content: { ends: sizing.contentEnds, sides: sizing.contentSides },
  application: { ends: sizing.ends, sides: sizing.sides },
} as const satisfies Record<ContextProp, { ends: string; sides: string }>;

/**
 * Returns the token string used for top/bottom (ends) padding for the given context and gutter.
 * Useful when another effect (e.g. fade mask) should align with the gutter padding.
 */
export function getGutterTokens(
  context: ContextProp,
  gutter: GutterProp,
): { ends: string; sides: string } {
  if (gutter === 'none') {
    return { ends: '$0', sides: '$0' };
  }
  return GUTTER_CONTEXT_TOKENS[context];
}

function getGutterCSSForContext(tokens: { ends: string; sides: string }, gutter: GutterProp): CSS {
  switch (gutter) {
    case 'all':
      return { padding: `${tokens.ends} ${tokens.sides}` };
    case 'vertical':
      return { padding: `${tokens.ends} 0` };
    case 'horizontal':
      return { padding: `0 ${tokens.sides}` };
    case 'top':
      return { paddingTop: tokens.ends };
    case 'bottom':
      return { paddingBottom: tokens.ends };
    case 'left':
      return { paddingLeft: tokens.sides };
    case 'right':
      return { paddingRight: tokens.sides };
    case 'none':
    default:
      return { padding: 0 };
  }
}

export const gutterCSS = ({ context = 'application', gutter = 'all' }: GutterProps = {}): CSS =>
  getGutterCSSForContext(GUTTER_CONTEXT_TOKENS[context], gutter);
